import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LaneData, ShootOffState } from '../models/Models';
import { Translation } from '../helper/WinfireLiveConstHelper';
import './ShootOffBanner.css';

/** Must match the `sob-slide-out` animation duration in ShootOffBanner.css */
const SLIDE_OUT_DURATION_MS = 500;

interface ShootOffBannerProps {
    /** Current shoot-off state from the server. Null or isActive=false = no shoot-off. */
    shootOffState: ShootOffState | undefined | null;
    /** All lane data — used to look up participant names for the tied lanes. */
    laneData: LaneData[] | undefined | null;
    /**
     * When true, the client has detected that the shoot-off is resolved even if the server
     * has not yet set isActive=false. Triggers the slide-out immediately.
     */
    overrideInactive?: boolean;
}

/**
 * Sports-TV style shoot-off announcement banner.
 *
 * Slides in from the top when a shoot-off starts (shootOffState.isActive becomes true).
 * Plays a slide-out animation when the shoot-off is resolved (isActive becomes false),
 * then unmounts after the animation completes.
 *
 * Displays:
 *   - "SHOOT-OFF" headline + round number
 *   - Names/lane numbers of the tied participants
 */
const ShootOffBanner: React.FC<ShootOffBannerProps> = ({ shootOffState, laneData, overrideInactive = false }) => {
    const { t } = useTranslation();

    const [isVisible, setIsVisible] = useState(false);
    const [isLeaving, setIsLeaving] = useState(false);
    const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Snapshot of the last active state — used during slide-out so the banner
    // continues to show the correct final round/participants.
    const [snapshot, setSnapshot] = useState<{ round: number; laneNumbers: number[] }>({
        round: 1,
        laneNumbers: [],
    });

    // Effective active state: server says active AND parent hasn't detected client-side resolution.
    const isActive = shootOffState?.isActive === true && !overrideInactive;

    // Stable string derived from lane numbers so the effect only re-runs when the set of
    // tied participants actually changes. The array reference from SignalR is new each push
    // (Object.is would always see a "change"), but sorting and joining produces a string that
    // React can diff by value. useMemo is intentionally avoided here because shootOffState
    // changes by reference every broadcast, so the memo cache would never hit.
    const laneNumbersKey = (shootOffState?.laneNumbers ?? []).slice().sort((a, b) => a - b).join(',');

    useEffect(() => {
        if (isActive) {
            // Cancel any pending unmount and show the banner
            if (leaveTimerRef.current) {
                clearTimeout(leaveTimerRef.current);
                leaveTimerRef.current = null;
            }
            setSnapshot({
                round: shootOffState?.currentShootOffRound ?? 1,
                laneNumbers: shootOffState?.laneNumbers ?? [],
            });
            setIsLeaving(false);
            setIsVisible(true);
        } else if (isVisible) {
            // Shoot-off just ended — play slide-out then unmount
            setIsLeaving(true);
            leaveTimerRef.current = setTimeout(() => {
                setIsVisible(false);
                setIsLeaving(false);
            }, SLIDE_OUT_DURATION_MS); // must match sob-slide-out duration in ShootOffBanner.css
        }
        return () => {
            if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive, shootOffState?.currentShootOffRound, laneNumbersKey]);
    // isActive already incorporates overrideInactive; laneNumbersKey is a stable sorted string.

    if (!isVisible) return null;

    const currentRound = snapshot.round;
    const tiedLaneNumbers = snapshot.laneNumbers;

    // Build participant labels and determine outcome coloring.
    // During slide-out (isLeaving) the shoot-off has just been resolved.
    // We read isEliminated from live laneData on every render — React will re-render
    // as soon as the server pushes the updated lane state, so any transient stale read
    // self-corrects within one render cycle during the 500 ms slide-out animation.
    // Three cases: eliminated (red), survived (green), unknown/missing lane (neutral base style).
    const participantLabels = tiedLaneNumbers.map(laneNr => {
        const ld = laneData?.find(l => l.laneNumber === laneNr);
        const name = ld?.participantName;
        const label = name ? `${name} (${laneNr})` : `${t(Translation.RANK)} ${laneNr}`;
        let chipClass = 'sob-participant-chip';
        if (isLeaving && ld !== undefined) {
            // Only apply outcome styling when we actually have lane data to read from;
            // missing/unknown lanes keep the neutral base class to avoid false "survived" green.
            chipClass += ld.isEliminated ? ' sob-participant-chip--eliminated' : ' sob-participant-chip--survived';
        }
        return { label, chipClass, laneNr };
    });

    const roundLabel = t(Translation.SHOOTOFF_ROUND, { round: currentRound });

    return (
        <div className="sob-overlay" role="alert" aria-live="assertive">
            <div className={`sob-banner${isLeaving ? ' sob-banner--leaving' : ''}`}>
                {/* Flame / alert icon */}
                <span className="sob-icon" aria-hidden="true">🔥</span>

                {/* Title + subtitle */}
                <div className="sob-text">
                    <span className="sob-title">{t(Translation.PHASE_SHOOTOFF)}</span>
                    <span className="sob-subtitle">
                        {roundLabel}{currentRound > 1 ? ` — ${t(Translation.SHOOTOFF_PARTICIPANTS)}` : ''}
                    </span>
                </div>

                {/* Participant chips */}
                {participantLabels.length > 0 && (
                    <div className="sob-participants" aria-label={t(Translation.SHOOTOFF_PARTICIPANTS)}>
                    {participantLabels.map(({ label, chipClass, laneNr }) => (
                        <span key={laneNr} className={chipClass}>{label}</span>
                    ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ShootOffBanner;


